import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        "Czy stać mnie na kredyt?" — to pytanie zadaje sobie każdy, kto myśli o własnym
        mieszkaniu. Odpowiedź zależy od wielu czynników, ale kluczowy jest jeden: ile
        zostajesz "na czysto" po wszystkich wydatkach. Sprawdzamy, jakie zarobki są potrzebne
        w 2026 roku, żeby realnie dostać kredyt hipoteczny.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Orientacyjne zarobki a kwota kredytu</h2>
      <p>
        Poniższe kwoty to przybliżone minimalne zarobki netto (na rękę) potrzebne do uzyskania
        kredytu hipotecznego w 2026 roku. Zakładamy singla bez innych zobowiązań, z 20% wkładem
        własnym i okresem 25 lat:
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-sm">Kredyt <strong>300 000 zł</strong></span>
            <span className="text-sm font-bold text-violet-700">~4 000–4 100 zł netto</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm">Kredyt <strong>400 000 zł</strong></span>
            <span className="text-sm font-bold text-violet-700">~5 300–5 500 zł netto</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm">Kredyt <strong>500 000 zł</strong></span>
            <span className="text-sm font-bold text-violet-700">~6 500–6 700 zł netto</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm">Kredyt <strong>600 000 zł</strong></span>
            <span className="text-sm font-bold text-violet-700">~8 000–8 500 zł netto</span>
          </div>
        </div>
      </div>

      <p className="mt-3">
        Przy dwóch osobach (np. małżeństwo) dochody się sumują — para zarabiająca łącznie
        10 000 zł netto może liczyć na kredyt w okolicach 450 000–500 000 zł.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} section="affordability">
          Sprawdź zdolność kredytową dla swoich zarobków
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Jak bank liczy <BlogLink slug="jak-obliczyc-zdolnosc-kredytowa">zdolność kredytową</BlogLink>?</h2>
      <p>
        Bank nie patrzy tylko na Twoją pensję. Liczy się to, ile zostaje Ci po pokryciu
        wszystkich kosztów. Oto co bierze pod uwagę:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Dochód netto</strong> — kwota "na rękę" po podatkach i składkach</li>
        <li><strong>Forma zatrudnienia</strong> — umowa o pracę na czas nieokreślony to ideał. Przy{' '}
          <BlogLink slug="kredyt-hipoteczny-dzialalnosc-b2b-zlecenie">umowie zlecenie, B2B
          i działalności gospodarczej</BlogLink> bank liczy dochód według własnej metody, co
          potrafi zmienić dostępną kwotę kredytu o kilkaset tysięcy złotych</li>
        <li><strong>Istniejące zobowiązania</strong> — raty innych kredytów, limity na kartach kredytowych (nawet niewykorzystane!), alimenty</li>
        <li><strong>Koszty utrzymania</strong> — bank przyjmuje minimalne koszty życia na osobę w gospodarstwie domowym</li>
        <li><strong>Liczba osób na utrzymaniu</strong> — każde dziecko zmniejsza zdolność kredytową</li>
        <li><strong>Historia kredytowa (BIK)</strong> — terminowe spłaty podnoszą scoring, opóźnienia go obniżają</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Złota zasada: rata nie powinna przekraczać 35% dochodu</h2>
      <p>
        Większość ekspertów i banków stosuje zasadę, że rata kredytu nie powinna przekraczać
        <strong> 30-35% Twojego miesięcznego dochodu netto</strong>. Powyżej tego progu:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>25% i mniej</strong> — komfortowa sytuacja, zostaje dużo na życie i oszczędności</li>
        <li><strong>25-35%</strong> — do udźwignięcia, ale trzeba planować budżet ostrożnie</li>
        <li><strong>35-50%</strong> — napięty budżet, bank może odmówić lub zaoferować gorsze warunki</li>
        <li><strong>Ponad 50%</strong> — ryzykowne, większość banków odrzuci wniosek</li>
      </ul>
      <p className="mt-3">
        Przykład: zarabiasz 8 000 zł netto. 35% to 2 800 zł — tyle maksymalnie powinna
        wynosić Twoja rata. Przy <BlogLink slug="co-to-jest-wibor">oprocentowaniu ~7,75%</BlogLink> na 25 lat to kredyt w okolicach 370 000 zł.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={460000} downPaymentPercent={20} section="affordability">
          Sprawdź ile wynosi Twój wskaźnik rata/dochód
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co obniża zdolność kredytową?</h2>
      <p>
        Nawet przy dobrych zarobkach kilka czynników może znacząco obniżyć Twoją zdolność:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Karta kredytowa z limitem</strong> — bank traktuje cały limit jako potencjalne zobowiązanie, nawet jeśli go nie używasz. Zrezygnuj z niepotrzebnych kart przed złożeniem wniosku</li>
        <li><strong>Kredyt na samochód lub ratalny</strong> — nawet rata 300 zł/mies. obniża zdolność o kilkadziesiąt tysięcy złotych</li>
        <li><strong>Krótki staż pracy</strong> — mniej niż 3-6 miesięcy u obecnego pracodawcy to za mało</li>
        <li><strong>Umowa na czas określony</strong> — bank może policzyć dochód tylko do końca umowy</li>
        <li><strong>Dużo osób w gospodarstwie</strong> — bank odejmuje koszty utrzymania za każdą osobę</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Jak zwiększyć zdolność kredytową?</h2>
      <p>
        Kilka praktycznych kroków, które możesz podjąć przed złożeniem wniosku:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Spłać istniejące zobowiązania</strong> — zamknij karty kredytowe, spłać raty. Każde 500 zł miesięcznych zobowiązań to ~50 000 zł mniej zdolności</li>
        <li><strong>Zwiększ <BlogLink slug="wklad-wlasny-kredyt-hipoteczny">wkład własny</BlogLink></strong> — im więcej wpłacisz, tym mniej musisz pożyczyć i tym niższa rata</li>
        <li><strong>Wydłuż <BlogLink slug="okres-kredytu-hipotecznego">okres kredytowania</BlogLink></strong> — 30 lat zamiast 25 obniża ratę (ale zwiększa całkowity koszt)</li>
        <li><strong>Weź kredyt we dwoje</strong> — dochody się sumują, co znacząco podnosi zdolność</li>
        <li><strong>Zadbaj o historię w BIK</strong> — terminowe spłaty nawet drobnych zobowiązań budują pozytywny scoring</li>
      </ul>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} loanYears={30} section="affordability">
          Sprawdź jak 30-letni kredyt obniża ratę
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        W 2026 roku na kredyt hipoteczny 400 000 zł potrzebujesz około 5 300–5 500 zł netto
        miesięcznie (singiel, bez zobowiązań). Ale Twoja realna zdolność zależy od wielu
        czynników — formy zatrudnienia, istniejących kredytów, liczby osób na utrzymaniu.
        Skorzystaj z kalkulatora, żeby sprawdzić jaki procent dochodu zajmie Twoja rata
        i czy mieści się w bezpiecznym progu.
      </p>
      <p className="mt-3">
        Wzięcie maksymalnego kredytu, na jaki bank Cię stać, to jeden z najczęstszych
        i najkosztowniejszych <BlogLink slug="najczestsze-bledy-kredyt-hipoteczny">błędów polskich kredytobiorców</BlogLink>{' '}
        — przy racie powyżej 35% dochodu netto jeden kryzys życiowy może prowadzić do
        zaległości i wpisu do BIK.
      </p>
      <p className="mt-3">
        Jeśli to Twój pierwszy kredyt hipoteczny, zobacz też kompletny poradnik{' '}
        <BlogLink slug="jak-dostac-kredyt-hipoteczny-na-pierwsze-mieszkanie">jak dostać kredyt
        hipoteczny na pierwsze mieszkanie</BlogLink> — z listą dokumentów, harmonogramem
        procesu i checklistą przed wnioskiem.
      </p>

      <div className="mt-4">
        <CalculatorLink section="affordability">
          Oblicz swoją zdolność kredytową w kalkulatorze RatyGo
        </CalculatorLink>
      </div>
    </>
  )
}
